import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import { useAuth } from '../../context/AuthContext';
import { mediatorAPI, sheetAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { todayIsoDate } from '../../utils/dateTime';

const MediatorDashboard = () => {
  const navigate = useNavigate();
  const { user, mediatorId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [downloadingSheet, setDownloadingSheet] = useState(false);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingRefunds: 0,
    activeDeals: 0,
    liveProducts: 0,
    overdueRefundAlerts: 0,
    urgentRefundAlerts: 0,
    closingSoonRefundAlerts: 0,
    refillRequests: 0,
    rejectAndRefill: 0,
    ordersWithPenalty: 0,
    refundFormsFilled: 0,
    approvedOrders: 0,
  });

  const resolveMediatorId = () =>
    mediatorId || JSON.parse(localStorage.getItem('mediatorData') || '{}')?.id;

  const fetchStats = async (medId = null) => {
    const effectiveMedId = medId || resolveMediatorId();
    if (!effectiveMedId) return;

    setLoading(true);
    try {
      const response = await mediatorAPI.getMediatorDashboardStats(effectiveMedId);
      const data = response.data.data || response.data;
      if (data) {
        setStats({
          totalOrders: data.totalOrders || 0,
          pendingRefunds: data.pendingRefunds || 0,
          activeDeals: data.availableDeals || 0,
          liveProducts: data.liveProducts || 0,
          overdueRefundAlerts: data.overdueRefundAlerts || 0,
          urgentRefundAlerts: data.urgentRefundAlerts || 0,
          closingSoonRefundAlerts: data.closingSoonRefundAlerts || 0,
          refillRequests: data.refillRequests || 0,
          rejectAndRefill: data.rejectAndRefill || 0,
          ordersWithPenalty: data.ordersWithPenalty || 0,
          refundFormsFilled: data.refundFormsFilled || 0,
          approvedOrders: data.approvedOrders || 0,
        });
      }
    } catch (error) {
      console.error('Failed to fetch mediator dashboard stats:', error);
      toast.error(error.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSheet = async () => {
    const medId = resolveMediatorId();
    if (!medId) return;

    setDownloadingSheet(true);
    try {
      const fileName = `Mediator_Orders_${todayIsoDate()}`;
      const response = await sheetAPI.generateSheet({
        fileName,
        mediatorId: medId,
        sheetFor: 'MEDIATOR',
      });
      const payload = response.data?.data || response.data;
      const url = payload?.sheetUrl || payload?.fileUrl;
      if (url) {
        window.open(url, '_blank');
        toast.success('Order sheet generated');
      } else {
        toast.success(response.data?.message || 'Sheet generated');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate sheet');
    } finally {
      setDownloadingSheet(false);
    }
  };

  useEffect(() => {
    const initialMedId = resolveMediatorId();
    if (initialMedId) {
      fetchStats(initialMedId);
    } else {
      const timer = setTimeout(() => {
        const retryMedId = resolveMediatorId();
        if (retryMedId) fetchStats(retryMedId);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [mediatorId]);

  const statCards = [
    { label: 'My Orders', value: stats.totalOrders, color: '#3b82f6', path: '/mediator/orders' },
    { label: 'Pending Refunds', value: stats.pendingRefunds, color: '#f59e0b', path: '/mediator/orders?status=ORDER_FORM_FILLED,ORDER_FORM_REFILL_REQUEST' },
    { label: 'Refund Forms Filled', value: stats.refundFormsFilled, color: '#0ea5e9', path: '/mediator/orders?status=REFUND_FORM_FILLED' },
    { label: 'Approved / Paid', value: stats.approvedOrders, color: '#10b981', path: '/mediator/orders?status=APPROVED,PAID,PAID_TO_BUYER,PAID_TO_MEDIATOR' },
    { label: 'Reject & Refill', value: stats.rejectAndRefill, color: '#be185d', path: '/mediator/orders?status=REJECT_AND_REFILL' },
    { label: 'Refill Requests', value: stats.refillRequests, color: '#ec4899', path: '/mediator/orders?status=ORDER_FORM_REFILL_REQUEST,REFUND_FORM_REFILL_REQUEST,REJECT_AND_REFILL' },
    { label: 'Penalty Orders', value: stats.ordersWithPenalty, color: '#dc2626', path: '/mediator/orders?penalty=1' },
    { label: 'My Deals', value: stats.liveProducts, color: '#8b5cf6', path: '/mediator/my-deals' },
    { label: 'Available Deals', value: stats.activeDeals, color: '#059669', path: '/mediator/available-deals' },
  ];

  return (
    <div style={{ padding: '28px', flex: 1, backgroundColor: '#f8fafc', overflow: 'auto' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
        <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: '36px', fontWeight: 800, color: '#1e293b', margin: '0 0 8px 0' }}>
              Mediator Panel
            </h1>
            <p style={{ fontSize: '18px', color: '#64748b', margin: 0 }}>
              Welcome, {user?.username || 'Mediator'}. Track orders, refunds, penalties, and deals.
            </p>
          </div>
          <Button onClick={() => fetchStats()} loading={loading} variant="secondary" icon="refresh">Refresh</Button>
        </div>

        {(stats.overdueRefundAlerts > 0 || stats.urgentRefundAlerts > 0 || stats.closingSoonRefundAlerts > 0) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            {stats.closingSoonRefundAlerts > 0 && (
              <div style={{
                padding: '18px 22px', borderRadius: '16px', background: '#fdf2f8',
                border: '1px solid #f9a8d4', display: 'flex', alignItems: 'center', gap: '14px'
              }}>
                <Icon name="clock" size={28} color="#db2777" />
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: '#9d174d' }}>
                    {stats.closingSoonRefundAlerts} refund form(s) closing within 5 days
                  </div>
                  <div style={{ fontSize: '15px', color: '#be185d', marginTop: '4px' }}>
                    Buyers have not filled refund forms yet. Remind them before the window closes.
                  </div>
                </div>
                <Button onClick={() => navigate('/mediator/orders?refundAlert=CLOSING_SOON')} style={{ marginLeft: 'auto', fontSize: '15px', padding: '12px 18px' }}>
                  View Orders
                </Button>
              </div>
            )}
            {stats.urgentRefundAlerts > 0 && (
              <div style={{
                padding: '18px 22px', borderRadius: '16px', background: '#fef2f2',
                border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '14px'
              }}>
                <Icon name="alert-circle" size={28} color="#dc2626" />
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: '#991b1b' }}>
                    {stats.urgentRefundAlerts} buyer(s) need urgent refund action
                  </div>
                  <div style={{ fontSize: '15px', color: '#b91c1c', marginTop: '4px' }}>
                    Penalty applies or refund window is closing. Contact buyers on WhatsApp.
                  </div>
                </div>
                <Button onClick={() => navigate('/mediator/orders?refundAlert=URGENT')} style={{ marginLeft: 'auto', fontSize: '15px', padding: '12px 18px' }}>
                  View Orders
                </Button>
              </div>
            )}
            {stats.overdueRefundAlerts > 0 && (
              <div style={{
                padding: '18px 22px', borderRadius: '16px', background: '#fffbeb',
                border: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: '14px'
              }}>
                <Icon name="alert-triangle" size={28} color="#d97706" />
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: '#92400e' }}>
                    {stats.overdueRefundAlerts} refund form(s) overdue (15+ days)
                  </div>
                  <div style={{ fontSize: '15px', color: '#b45309', marginTop: '4px' }}>
                    Buyers have not filled refund forms yet. Remind them before penalties apply.
                  </div>
                </div>
                <Button onClick={() => navigate('/mediator/orders?refundAlert=OVERDUE')} style={{ marginLeft: 'auto', fontSize: '15px', padding: '12px 18px' }}>
                  View Orders
                </Button>
              </div>
            )}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '20px',
          marginBottom: '28px',
        }}>
          {statCards.map((card) => (
            <div
              key={card.label}
              onClick={() => navigate(card.path)}
              style={{
                background: '#fff', borderRadius: '18px', padding: '24px',
                border: '1px solid #e2e8f0', cursor: 'pointer',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08)',
              }}
            >
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#64748b', marginBottom: '12px' }}>
                {card.label}
              </div>
              <div style={{ fontSize: '36px', fontWeight: 800, color: card.color }}>
                {loading ? '...' : card.value}
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: '#fff', borderRadius: '18px', padding: '24px', border: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: '22px', fontWeight: 800, color: '#1e293b', marginBottom: '20px' }}>
            Quick Actions
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <Button onClick={() => navigate('/mediator/orders')} icon="sub" style={{ fontSize: '16px', padding: '16px' }}>
              View All Orders
            </Button>
            <Button onClick={() => navigate('/mediator/my-deals')} variant="secondary" icon="package" style={{ fontSize: '16px', padding: '16px' }}>
              My Deals
            </Button>
            <Button onClick={() => navigate('/mediator/available-deals')} variant="secondary" icon="deals" style={{ fontSize: '16px', padding: '16px' }}>
              Browse Deals
            </Button>
            <Button onClick={handleDownloadSheet} loading={downloadingSheet} variant="secondary" icon="file" style={{ fontSize: '16px', padding: '16px' }}>
              Download Order Sheet
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediatorDashboard;
